import { cachedParamOid, cachedParams } from '../../sampling/sampleRegistration/SampleLoginView';
import {
  guideFirstDefault, guidePreservFor, guideSets, isFieldTest,
  type BottleLookups, type SrBottleDetail, type SrSample, type SrTest,
} from '../../sampling/sampleRegistration/srData';
import type {
  PreLoginBottle, PreLoginBottleInput, PreLoginParameter, PreLoginSample,
  PreLoginTest, PreLoginTestInput,
} from './types';

/**
 * Adapter between a Pre-Login COC's server rows and Sample Registration's
 * in-memory model (`SrSample` / `SrTest`).
 *
 * The Test Assignment window, the Containers window / tab and the parameter
 * checklist are Registration's OWN components (standing rule 8 — one
 * implementation, so the two screens cannot drift). They are pure functions of
 * `SrSample[]`, so this file only translates in and out:
 *
 *  · {@link buildSrSamples} — `COCSettingsSamples` + `COCSettingsTest` (one row
 *    per PARAMETER) + `COCSettingsBottleAllocation` (one row per bottle letter)
 *    → samples that each hold one `SrTest` per (TestMethod, Component).
 *  · {@link testsToInputs} / {@link bottlesToInputs} — the reverse, producing the
 *    existing whole-set PUT bodies.
 */

/** Stable test key — also what the BottleID cell hands the Containers window. */
export const srTestKey = (testMethodOid: string | null | undefined, comp: string | null | undefined): string =>
  `${(testMethodOid ?? '').toLowerCase()}|${comp || 'Default'}`;

const norm = (s: string | null | undefined) => (s ?? '').trim().toLowerCase();

/** What was loaded, kept so a save can reuse the original row Oids. */
export interface PreLoginSrOrigin {
  /** `${sampleOid}|${testKey}` → the parameter rows behind that test. */
  tests: Map<string, PreLoginTest[]>;
  /** `${sampleOid}|${testMethodOid}|${letter}` → bottle row Oid. */
  bottles: Map<string, string>;
  /** Sample display id → Oid. */
  sampleOidById: Map<string, string>;
}

export interface PreLoginSrModel { samples: SrSample[]; origin: PreLoginSrOrigin }

export function buildSrSamples(
  samples: PreLoginSample[], tests: PreLoginTest[], bottles: PreLoginBottle[],
  params: PreLoginParameter[], fieldKeys: Set<string>,
): PreLoginSrModel {
  const origin: PreLoginSrOrigin = { tests: new Map(), bottles: new Map(), sampleOidById: new Map() };

  // Saved analyte names per (sample, test, method, component) — the Parameters
  // popup restores a customised selection from these.
  const names = new Map<string, string[]>();
  for (const p of params) {
    const k = `${p.sampleOid}|${norm(p.test)}|${norm(p.method)}|${norm(p.component) || 'default'}`;
    const list = names.get(k) ?? [];
    if (p.parameter) list.push(p.parameter);
    names.set(k, list);
  }

  const out: SrSample[] = samples.map(s => {
    const id = s.sampleID ?? '';
    origin.sampleOidById.set(id, s.oid);

    const mine = tests.filter(t => t.sampleOid === s.oid);
    const byKey = new Map<string, PreLoginTest[]>();
    for (const r of mine) {
      const k = srTestKey(r.testMethodOid ?? r.test, r.components);
      const list = byKey.get(k) ?? [];
      list.push(r);
      byKey.set(k, list);
    }

    const myBottles = bottles.filter(b => b.sampleOid === s.oid);
    const srTests: SrTest[] = [];
    for (const [key, rows] of byKey) {
      origin.tests.set(`${s.oid}|${key}`, rows);
      const r = rows[0]!;
      const comp = r.components || 'Default';
      const tm = r.testMethodOid ?? undefined;

      const bs = myBottles.filter(b => tm && norm(b.testMethodOid) === norm(tm));
      const letters = [...new Set(bs.map(b => (b.bottleIdInternal ?? '').trim()).filter(Boolean))].sort();
      const bd: Record<string, SrBottleDetail> = {};
      for (const b of bs) {
        const l = (b.bottleIdInternal ?? '').trim();
        if (!l) continue;
        if (tm) origin.bottles.set(`${s.oid}|${norm(tm)}|${l}`, b.oid);
        bd[l] = {
          container: b.containerName ?? '', preserv: b.preservativeName ?? '',
          storage: b.storageName ?? '', storageCondition: b.storageConditionName ?? '',
        };
      }
      const first = letters[0] ? bd[letters[0]] : undefined;
      const hasFill = letters.some(l => bd[l]?.container || bd[l]?.preserv);

      const savedNames = names.get(`${s.oid}|${norm(r.test)}|${norm(r.method)}|${norm(comp)}`) ?? [];
      srTests.push({
        key, name: r.test ?? '', method: r.method ?? '', comp,
        testGroupName: r.testGroupName ?? '',
        bottle: letters.join(','),
        container: first?.container ?? '', preserv: first?.preserv ?? '', storage: first?.storage ?? '',
        bd: letters.length ? bd : null,
        params: null,
        savedParamNames: savedNames.length ? savedNames : undefined,
        paramData: {},
        hdTime: '', status: r.subOut ? 'Sub Out' : '', remark: false, subOut: r.subOut,
        added: '', hold: false,
        testMethodOid: tm,
        isFieldTest: fieldKeys.has(`${norm(r.test)}|${norm(r.method)}|${norm(comp)}`) || undefined,
        // Only a bottle that already carries a container / preservative is authoritative;
        // an empty one (generated blank by the server) still takes the Test Guide default.
        hasSavedBottle: hasFill || undefined,
      });
    }

    return {
      id, hold: s.hold,
      // Qty is a stored, user-owned value here (never auto-derived from the letters).
      v: {
        Qty: String(s.qty ?? 1), __qtyManual: '1',
        SampleMatrix: s.visualMatrixOid ?? '', VisualMatrix: s.visualMatrixOid ?? '',
      },
      tests: srTests,
    };
  });

  return { samples: out, origin };
}

/** Restore a customised analyte selection once the test's analyte list is cached
 *  (a strict subset → params map, the full set → null = All Param). Mirrors the
 *  restore step Sample Registration's own Sample Login view runs after a load. */
export function restoreParams(samples: SrSample[]): SrSample[] {
  return samples.map(s => ({
    ...s,
    tests: s.tests.map(t => {
      if (!t.savedParamNames) return t;
      const list = cachedParams(t);
      if (!list) return t;
      const saved = new Set(t.savedParamNames);
      const c: SrTest = { ...t, savedParamNames: undefined };
      const all = list.every(n => saved.has(n));
      if (!all && list.some(n => saved.has(n))) c.params = Object.fromEntries(list.map(n => [n, saved.has(n)]));
      return c;
    }),
  }));
}

/** Pre-fill the TOP Container / Preservative the Test Guide lists for each test
 *  that has none yet (Registration's owner-decided rule — a new test never lands
 *  with a blank Container / Preservative). A bottle that already carries a value
 *  is authoritative and untouched. Pure: returns new samples. */
export function applyGuide(samples: SrSample[], bl: BottleLookups): SrSample[] {
  if (bl.testGuide.length === 0) return samples;
  const { containers, preservatives } = guideSets(bl.testGuide);
  return samples.map(s => ({
    ...s,
    tests: s.tests.map(t => {
      if (!t.testMethodOid || t.hasSavedBottle || isFieldTest(t)) return t;
      const gc = guideFirstDefault(containers.get(t.testMethodOid));
      const gp = (gc !== undefined ? guidePreservFor(bl.testGuide, t.testMethodOid, gc) : undefined)
        ?? guideFirstDefault(preservatives.get(t.testMethodOid));
      if (gc === undefined && gp === undefined) return t;
      const c: SrTest = { ...t, bd: t.bd ? { ...t.bd } : null };
      if (gc !== undefined) c.container = gc;
      if (gp !== undefined) c.preserv = gp;
      // Carry the fill into every bottle letter that is still empty.
      if (c.bd) {
        for (const l of Object.keys(c.bd)) {
          const d = c.bd[l]!;
          c.bd[l] = { ...d, container: d.container || c.container, preserv: d.preserv || c.preserv };
        }
      }
      return c;
    }),
  }));
}

// ── Back to the wire ───────────────────────────────────────────────────────

/** One sample's tests → the whole-set PUT body. Existing parameter rows are
 *  reused (their Oids and per-row fields survive); analytes ticked on are added;
 *  analytes ticked off are dropped. Requires each test's analyte list to be
 *  cached (the Test Assignment window guarantees it before it saves). */
export function testsToInputs(
  sampleOid: string, next: SrTest[], origin: PreLoginSrOrigin,
): PreLoginTestInput[] {
  const out: PreLoginTestInput[] = [];
  const asInput = (r: PreLoginTest, subOut: boolean): PreLoginTestInput => ({
    oid: r.oid.startsWith('new:') ? null : r.oid,
    testParameterOid: r.testParameterOid ?? '',
    groupTestOid: r.groupTestOid, isGroup: r.isGroup, subOut,
    subLabOid: r.subLabOid, suboutQCTypeOid: r.suboutQCTypeOid,
    preservativeOid: r.preservativeOid, containerTypeOid: r.containerTypeOid,
    containerCount: r.containerCount, dupBottle: r.dupBottle,
    unitOid: null, mdl: null, rptLimit: null, df: null,
  });

  for (const t of next) {
    const orig = origin.tests.get(`${sampleOid}|${t.key}`) ?? [];
    const names = cachedParams(t);
    const included = names ? (t.params ? names.filter(n => t.params![n] !== false) : names) : null;
    const wanted = included
      ? included.map(n => cachedParamOid(t, n)).filter((o): o is string => !!o)
      : null;

    // Not resolvable (no analyte list / no Oids) → keep exactly what was stored.
    if (!wanted || wanted.length === 0 || (included && wanted.length !== included.length)) {
      for (const r of orig) out.push(asInput(r, t.subOut));
      continue;
    }

    const have = new Map(orig.map(r => [norm(r.testParameterOid), r]));
    for (const oid of wanted) {
      const r = have.get(norm(oid));
      if (r) { out.push(asInput(r, t.subOut)); continue; }
      out.push({
        oid: null, testParameterOid: oid,
        groupTestOid: orig[0]?.groupTestOid ?? null, isGroup: orig[0]?.isGroup ?? false,
        subOut: t.subOut, subLabOid: null, suboutQCTypeOid: null,
        preservativeOid: null, containerTypeOid: null, containerCount: null, dupBottle: null,
        unitOid: null, mdl: null, rptLimit: null, df: null,
      });
    }
  }
  return out;
}

/** One sample's bottle allocation → the whole-set PUT body. */
export function bottlesToInputs(
  sample: SrSample, sampleOid: string, origin: PreLoginSrOrigin, bl: BottleLookups,
): PreLoginBottleInput[] {
  const oidOf = (list: { oid: string; name: string }[], name: string) =>
    name ? list.find(x => x.name === name)?.oid ?? null : null;
  const out: PreLoginBottleInput[] = [];
  for (const t of sample.tests) {
    if (!t.testMethodOid) continue;
    const letters = (t.bottle || '').split(',').map(x => x.trim()).filter(Boolean);
    for (const l of letters) {
      const d = t.bd?.[l] ?? { container: t.container, preserv: t.preserv, storage: t.storage, storageCondition: '' };
      out.push({
        oid: origin.bottles.get(`${sampleOid}|${norm(t.testMethodOid)}|${l}`) ?? null,
        bottleId: l,
        testMethodOid: t.testMethodOid,
        containerOid: oidOf(bl.containers, d.container),
        preservativeOid: oidOf(bl.preservatives, d.preserv),
        storageOid: oidOf(bl.storages, d.storage),
        storageConditionOid: oidOf(bl.storageConditions, d.storageCondition),
      });
    }
  }
  return out;
}

/** Cheap signature of a sample's bottle state, to detect which samples changed. */
export function bottleSignature(s: SrSample): string {
  return JSON.stringify([
    s.v.Qty,
    s.tests.map(t => [t.key, t.bottle, t.container, t.preserv, t.storage, t.bd]),
  ]);
}
